import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Plus, Building2, TrendingUp, Wallet, HardHat, MapPin, ArrowRight, Search, LayoutGrid, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ScadenzeDocumentiCard from "@/components/ScadenzeDocumentiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATI = ["In corso", "In pausa", "Completato"];
const STATO_COLORS = {
  "In corso": "text-red-700 border-red-200 bg-red-50",
  "In pausa": "text-amber-700 border-amber-200 bg-amber-50",
  "Completato": "text-emerald-700 border-emerald-200 bg-emerald-50",
};

const STATO_DOT_COLORS = {
  "In corso": "bg-red-500",
  "In pausa": "bg-yellow-400",
  "Completato": "bg-green-500",
};

const formattaDataCantiere = (valore) => {
  if (!valore) return "Data non indicata";
  const soloData = String(valore).slice(0, 10);
  const parti = soloData.split("-");
  if (parti.length !== 3) return soloData;
  return `${parti[2]}/${parti[1]}/${parti[0]}`;
};

const dataInizioEffettiva = (cantiere) =>
  cantiere?.data_inizio || cantiere?.created_at || null;

const durataCantiere = (inizio, fine) => {
  if (!inizio || !fine) return null;
  const dataInizio = new Date(`${String(inizio).slice(0, 10)}T00:00:00Z`);
  const dataFine = new Date(`${String(fine).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(dataInizio.getTime()) || Number.isNaN(dataFine.getTime())) return null;
  const differenza = Math.floor((dataFine - dataInizio) / 86400000);
  return Math.max(1, differenza + 1);
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cantieri, setCantieri] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", cliente: "", indirizzo: "", data_inizio: "", budget: "", stato: "In corso", completed_at: "", descrizione: "", fuori_budget: false });
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [statoFilter, setStatoFilter] = useState("Tutti");
  const [viewMode, setViewMode] = useState(() =>
    ["active", "paused", "completed"].includes(location.state?.dashboardView)
      ? location.state.dashboardView
      : "active"
  );
  const [completedYear, setCompletedYear] = useState(() =>
    location.state?.completedYear || String(new Date().getFullYear())
  );
  const showCompleted = viewMode === "completed";
  const showPaused = viewMode === "paused";

  const load = () => api.get("/cantieri").then((r) => setCantieri(Array.isArray(r.data) ? r.data : [])).catch(() => setCantieri([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/cantieri", { ...form, budget: parseFloat(form.budget) || 0 });
      toast.success("Cantiere creato");
      setOpen(false);
      setForm({ nome: "", cliente: "", indirizzo: "", data_inizio: "", budget: "", stato: "In corso", completed_at: "", descrizione: "", fuori_budget: false });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeCantiere = async (e, cantiere) => {
    e.stopPropagation();

    const conferma = window.confirm(
      `Vuoi eliminare definitivamente il cantiere "${cantiere.nome}"?

Saranno eliminati anche manodopera, materiali, lavorazioni e foto collegate.`
    );

    if (!conferma) return;

    try {
      await api.delete(`/cantieri/${cantiere.id}`);
      setCantieri((elenco) =>
        elenco.filter((item) => item.id !== cantiere.id)
      );
      toast.success("Cantiere eliminato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const activeCantieri = (cantieri || []).filter((c) => c.stato === "In corso");

  const pausedCantieri = (cantieri || []).filter((c) => c.stato === "In pausa");

  const completedCantieri = (cantieri || []).filter(
    (c) => c.stato === "Completato"
  );

  const completedYears = [...new Set(completedCantieri.map((c) =>
    String(c.completed_at || c.data_fine || c.data_inizio || c.created_at || "").slice(0, 4)
  ).filter((anno) => /^\d{4}$/.test(anno)))].sort((a, b) => Number(b) - Number(a));

  const completedCantieriAnno = completedYear === "all"
    ? completedCantieri
    : completedCantieri.filter((c) =>
        String(c.completed_at || c.data_fine || c.data_inizio || c.created_at || "").slice(0, 4) === completedYear
      );

  const completedCantieriContabilizzati = completedCantieriAnno.filter(
    (c) => !c.fuori_budget
  );

  const visibleCantieri = [
    ...(showCompleted ? completedCantieriAnno : showPaused ? pausedCantieri : activeCantieri),
  ].sort((a, b) => {
    const dataA = new Date(a.ultima_modifica || a.updated_at || a.created_at || 0).getTime();
    const dataB = new Date(b.ultima_modifica || b.updated_at || b.created_at || 0).getTime();
    return dataB - dataA;
  });

  const visibleCantieriContabilizzati = visibleCantieri.filter((c) => !c.fuori_budget);

  const totals = visibleCantieriContabilizzati.reduce(
    (a, c) => ({
      totale: a.totale + (c.costo_totale || 0),
      mano: a.mano + (c.costo_manodopera || 0),
      mat: a.mat + (c.costo_materiali || 0),
      attrezzature:
        a.attrezzature + (c.costo_attrezzature || 0),
    }),
    { totale: 0, mano: 0, mat: 0, attrezzature: 0 }
  );

  const completedTotals = completedCantieriContabilizzati.reduce(
    (totali, cantiere) => {
      const importoLavori =
        Number(cantiere.budget) || 0;

      const lavoriExtra = (
        Array.isArray(cantiere.lavori_extra) ? cantiere.lavori_extra : []
      ).reduce(
        (somma, extra) =>
          somma + (Number(extra.importo) || 0),
        0
      );

      const totaleLavori =
        importoLavori + lavoriExtra;

      const totaleSpese =
        Number(cantiere.costo_totale) || 0;

      return {
        lavori: totali.lavori + totaleLavori,
        spese: totali.spese + totaleSpese,
      };
    },
    {
      lavori: 0,
      spese: 0,
    }
  );

  const utileComplessivo =
    completedTotals.lavori -
    completedTotals.spese;

  const percentualeUtileComplessiva =
    completedTotals.lavori > 0
      ? (
          utileComplessivo /
          completedTotals.lavori
        ) * 100
      : 0;

  const completedChartData =
    completedTotals.lavori > 0
      ? utileComplessivo >= 0
        ? [
            {
              name: "Spese",
              value: Math.min(
                completedTotals.spese,
                completedTotals.lavori
              ),
            },
            {
              name: "Utile",
              value: utileComplessivo,
            },
          ].filter((voce) => voce.value > 0)
        : [
            {
              name: "Spese",
              value: completedTotals.spese,
            },
          ]
      : [];

  const activeStats = [
    {
      label: showPaused ? "Cantieri in pausa" : "Cantieri attivi",
      value: visibleCantieriContabilizzati.length,
      icon: Building2,
      color: "text-secondary",
    },
    {
      label: "Costo manodopera",
      value: eur(totals.mano),
      icon: HardHat,
      color: "text-primary",
    },
    {
      label: "Costo materiali",
      value: eur(totals.mat),
      icon: Wallet,
      color: "text-secondary",
    },
    {
      label: "Costo attrezzature",
      value: eur(totals.attrezzature),
      icon: Building2,
      color: "text-secondary",
    },
    {
      label: "Costo totale",
      value: eur(totals.totale),
      icon: TrendingUp,
      color: "text-primary",
    },
  ];

  const completedStats = [
    {
      label: "Totale lavori completati",
      value: eur(completedTotals.lavori),
      icon: Wallet,
      color: "text-primary",
    },
    {
      label: "Cantieri completati",
      value: completedCantieriContabilizzati.length,
      icon: Building2,
      color: "text-emerald-600",
    },
    {
      label:
        utileComplessivo >= 0
          ? "Utile complessivo"
          : "Perdita complessiva",
      value: eur(Math.abs(utileComplessivo)),
      icon: TrendingUp,
      color:
        utileComplessivo >= 0
          ? "text-emerald-600"
          : "text-red-600",
    },
  ];

  const stats = showCompleted
    ? completedStats
    : activeStats;

  const filtered = visibleCantieri.filter((c) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || [c.nome, c.cliente, c.indirizzo].some((f) => (f || "").toLowerCase().includes(q));
    const matchS = statoFilter === "Tutti" || c.stato === statoFilter;
    return matchQ && matchS;
  });

  return (
    <AppShell wide>
      {/* dashboard-con-elenco-scrollabile */}
      <div className="h-[calc(100dvh-6rem)] min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5 lg:flex-none">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Dashboard</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold">{showCompleted ? "Cantieri completati" : showPaused ? "Cantieri in pausa" : "I tuoi cantieri"}</h1>
            {showCompleted && (
              <Select value={completedYear} onValueChange={setCompletedYear}>
                <SelectTrigger data-testid="completed-year-filter" className="h-10 w-40 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli anni</SelectItem>
                  {[...new Set([String(new Date().getFullYear()), ...completedYears])].map((anno) => (
                    <SelectItem key={anno} value={anno}>Anno {anno}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <Button
          variant={showCompleted ? "default" : "outline"}
          data-testid="completed-cantieri-button"
          onClick={() => {
            setViewMode(showCompleted ? "active" : "completed");
            setStatoFilter("Tutti");
            setQuery("");
          }}
          className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]"
        >
          {showCompleted
            ? "Torna ai cantieri attivi"
            : `Cantieri completati (${completedCantieri.length})`}
        </Button>

        <Button
          variant={showPaused ? "default" : "outline"}
          data-testid="paused-cantieri-button"
          onClick={() => {
            setViewMode(showPaused ? "active" : "paused");
            setStatoFilter("Tutti");
            setQuery("");
          }}
          className="rounded-md font-bold h-11 px-5 transition-transform active:scale-[0.98]"
        >
          {showPaused ? "Torna ai cantieri attivi" : `Cantieri in pausa (${pausedCantieri.length})`}
        </Button>

        <Button variant="outline" data-testid="magazzino-link" onClick={() => navigate("/gestionale/magazzino")}
          className="rounded-md font-bold h-11 px-5 transition-transform active:scale-[0.98]">
          <Warehouse className="h-4 w-4 mr-2" /> Magazzino
        </Button>

        <Button variant="outline" data-testid="gestionale-link" onClick={() => navigate("/gestionale")}
          className="rounded-md font-bold h-11 px-5 transition-transform active:scale-[0.98]">
          <LayoutGrid className="h-4 w-4 mr-2" /> Gestionale
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-cantiere-button" className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]">
              <Plus className="h-4 w-4 mr-2" /> Nuovo Cantiere
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Nuovo Cantiere</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Nome cantiere *</Label>
                <Input data-testid="cantiere-nome-input" value={form.nome} required
                  onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Villa Bianchi" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} placeholder="Sig. Bianchi" />
                </div>
                <div className="space-y-2">
                  <Label>Importo lavori (EUR)</Label>
                  <Input type="number" step="0.01" value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="150000" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Indirizzo</Label>
                <Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} placeholder="Via Roma 12, Milano" />
              </div>
              <div className="space-y-2">
                <Label>Data inizio lavori *</Label>
                <Input
                  type="date"
                  required
                  value={form.data_inizio}
                  onChange={(e) => setForm({ ...form, data_inizio: e.target.value })}
                  data-testid="cantiere-data-inizio-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v, completed_at: v === "Completato" ? (form.completed_at || new Date().toISOString().slice(0, 10)) : "" })}>
                  <SelectTrigger data-testid="cantiere-stato-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.stato === "Completato" && (
                <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <Label>Data di completamento *</Label>
                  <Input type="date" required value={form.completed_at} onChange={(e) => setForm({ ...form, completed_at: e.target.value })} data-testid="cantiere-completed-at-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} rows={2} />
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <input
                  type="checkbox"
                  checked={form.fuori_budget}
                  onChange={(e) => setForm({ ...form, fuori_budget: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-amber-600"
                />
                <span>
                  <span className="block font-bold text-amber-900">Fuori budget</span>
                  <span className="block text-xs text-amber-800">Esclude il cantiere dai totali, dai grafici e dai rendiconti complessivi.</span>
                </span>
              </label>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="save-cantiere-button" className="rounded-md font-bold">
                  {saving ? "Salvataggio..." : "Crea Cantiere"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Ricerca fissa sotto titolo e pulsanti */}
      {cantieri && visibleCantieri.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5 lg:flex-none">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="cantieri-search-input" value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, cliente o indirizzo..."
              className="pl-9 h-11 rounded-md" />
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-2 pb-6">
      {/* Stats */}
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${showCompleted ? "lg:grid-cols-3" : "lg:grid-cols-5"} gap-3 mb-6`}>
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="rounded-xl border border-border bg-card px-4 py-3 min-h-[84px]">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-[10px] leading-tight tracking-[0.12em] uppercase font-bold text-muted-foreground">{s.label}</p>
              <s.icon className={`h-4 w-4 shrink-0 ${s.color}`} />
            </div>
            <p className="font-heading text-xl sm:text-2xl font-bold tracking-tight">{s.value}</p>
          </motion.div>
        ))}

        {!showCompleted && <ScadenzeDocumentiCard />}
      </div>

      {showCompleted && (
        <div className="rounded-xl border border-border bg-card p-6 mb-10">
          <div className="mb-4">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
              Redditività complessiva
            </p>

            <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">
              Percentuale utili dei cantieri completati
            </h2>

            <p className="text-sm text-muted-foreground mt-1">
              Rapporto tra i lavori completati e tutte le spese
              di manodopera, materiali e attrezzature.
            </p>
          </div>

          {completedTotals.lavori <= 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Inserisci l'importo lavori nei cantieri completati
              per calcolare la percentuale.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
              <div className="relative min-h-[320px]">
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={completedChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={115}
                      innerRadius={78}
                      paddingAngle={3}
                      startAngle={90}
                      endAngle={-270}
                    >
                      {completedChartData.map((voce) => (
                        <Cell
                          key={voce.name}
                          fill={
                            voce.name === "Utile"
                              ? "#10B981"
                              : utileComplessivo < 0
                                ? "#EF4444"
                                : "#FF5A00"
                          }
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      formatter={(valore) => eur(valore)}
                      contentStyle={{
                        borderRadius: 8,
                        border:
                          "1px solid hsl(var(--border))",
                        background:
                          "hsl(var(--card))",
                      }}
                    />

                    <Legend />
                  </PieChart>
                </ResponsiveContainer>

                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-8">
                  <span
                    className={`font-heading text-4xl font-bold ${
                      percentualeUtileComplessiva >= 0
                        ? "text-emerald-600"
                        : "text-red-600"
                    }`}
                  >
                    {percentualeUtileComplessiva.toFixed(1)}%
                  </span>

                  <span className="text-sm text-muted-foreground mt-1">
                    {percentualeUtileComplessiva >= 0
                      ? "Utile"
                      : "Perdita"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl border border-border bg-muted/20 p-5">
                  <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                    Totale lavori completati
                  </p>

                  <p className="font-heading text-3xl font-bold mt-2">
                    {eur(completedTotals.lavori)}
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-muted/20 p-5">
                  <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                    Spese complessive
                  </p>

                  <p className="font-heading text-3xl font-bold mt-2">
                    {eur(completedTotals.spese)}
                  </p>
                </div>

                <div
                  className={`rounded-xl border p-5 ${
                    utileComplessivo >= 0
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  <p
                    className={`text-xs tracking-[0.15em] uppercase font-bold ${
                      utileComplessivo >= 0
                        ? "text-emerald-700"
                        : "text-red-700"
                    }`}
                  >
                    {utileComplessivo >= 0
                      ? "Utile complessivo"
                      : "Perdita complessiva"}
                  </p>

                  <p
                    className={`font-heading text-3xl font-bold mt-2 ${
                      utileComplessivo >= 0
                        ? "text-emerald-700"
                        : "text-red-700"
                    }`}
                  >
                    {eur(Math.abs(utileComplessivo))}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div>
      {/* List */}
      {cantieri === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : visibleCantieri.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-16 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="font-heading text-xl font-semibold mt-4">{showCompleted ? "Nessun cantiere completato" : showPaused ? "Nessun cantiere in pausa" : "Nessun cantiere attivo"}</p>
          <p className="text-muted-foreground mt-1">{showCompleted ? "I cantieri completati compariranno qui." : showPaused ? "I cantieri messi in pausa compariranno qui." : "Crea un nuovo cantiere per iniziare."}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-16 text-center" data-testid="no-results">
          <Search className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="font-heading text-xl font-semibold mt-4">Nessun risultato</p>
          <p className="text-muted-foreground mt-1">Nessun cantiere corrisponde alla ricerca.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`cantiere-card-${c.id}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => navigate(`/cantiere/${c.id}`, {
                state: {
                  dashboardView: viewMode,
                  completedYear,
                },
              })}
              className="group text-left rounded-xl border border-border bg-card p-4 h-full transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-primary/40"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-heading text-lg font-bold leading-tight tracking-tight group-hover:text-primary transition-colors">{c.nome}</h3>
                <div className="flex flex-wrap justify-end gap-1.5">
                  {c.fuori_budget && <Badge className="px-2 py-0.5 text-[10px] border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100">Fuori budget</Badge>}
                  {c.stato === "Completato" && !c.fuori_budget && Math.abs(Number(c.da_incassare || 0)) >= 0.005 && <Badge title={`Da incassare: ${eur(c.da_incassare)}`} className="px-2 py-0.5 text-[10px] border border-red-300 bg-red-100 text-red-700 hover:bg-red-100">Incassare</Badge>}
                  <Badge variant="outline" className={`shrink-0 border flex items-center gap-1.5 px-2 py-0.5 text-[10px] ${STATO_COLORS[c.stato] || ""}`}>
                    <span className={`h-2 w-2 rounded-full ${STATO_DOT_COLORS[c.stato] || "bg-gray-400"}`} />
                    {c.stato}
                  </Badge>
                </div>
              </div>
              {c.indirizzo && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-3 line-clamp-1">
                  <MapPin className="h-3.5 w-3.5 shrink-0" /> {c.indirizzo}
                </p>
              )}

              {!showCompleted && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1 text-xs">
                  <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    Data inizio lavori
                  </span>
                  <span className="whitespace-nowrap font-semibold">
                    {formattaDataCantiere(dataInizioEffettiva(c))}
                  </span>
                </div>
              )}

              

      {showCompleted && (
                <div className="mb-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <span className="whitespace-nowrap">
                      <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Inizio</span>
                      <span className="font-semibold">{formattaDataCantiere(dataInizioEffettiva(c))}</span>
                    </span>
                    <span className="whitespace-nowrap">
                      <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Fine</span>
                      <span className="font-semibold">{formattaDataCantiere(c.completed_at)}</span>
                    </span>
                    <span className="whitespace-nowrap">
                      <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Durata</span>
                      <span className="font-semibold">
                        {durataCantiere(dataInizioEffettiva(c), c.completed_at)
                          ? `${durataCantiere(dataInizioEffettiva(c), c.completed_at)} giorni`
                          : "Non disponibile"}
                      </span>
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-1.5 pt-3 border-t border-border">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Manodopera</span>
                  <span className="font-semibold">{eur(c.costo_manodopera)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Materiali</span>
                  <span className="font-semibold">{eur(c.costo_materiali)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Attrezzature</span>
                  <span className="font-semibold">{eur(c.costo_attrezzature)}</span>
                </div>
                <div className="flex justify-between items-center pt-1.5">
                  <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Totale</span>
                  <span className="font-heading text-lg font-bold text-primary">{eur(c.costo_totale)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3">
                <span className="flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Apri <ArrowRight className="h-3.5 w-3.5" />
                </span>

                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`delete-cantiere-${c.id}`}
                  onClick={(e) => removeCantiere(e, c)}
                  className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-600 hover:text-white"
                >
                  Elimina
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      )}
      </div>
      </div>
      </div>
    </AppShell>
  );
}
